import * as THREE from 'three';
import type { UnitDefinition } from '../../../shared/game/world-types.js';
import type { HumanModelOptions } from './human-model.js';

/**
 * Record and replay of a figure's slow, deterministic geometry work, so a figure baked ahead (`figure-bakes.ts`) skips it. Builders wrap
 * each such step in `taped`: while a figure records, the step runs and its result is kept in call order; while it replays, the step is
 * skipped and the kept result returned in its place. The same code and the same definition take the same steps, so the n-th call always
 * asks for the n-th result. Each result is kept under its step's name: a tape whose steps do not line up with the builder's (recorded
 * from other code), or that fails the builder in any way, is dropped and the figure rebuilt live.
 */

/** A kept value: JSON, with typed arrays and geometries as tagged objects. An array is the array itself until it is written to a file, then its index there. */
type Packed = null | boolean | number | string | TypedArray | Packed[] | { [key: string]: Packed };
type TypedArray = Float32Array | Uint32Array | Uint16Array | Uint8Array | Int32Array | Int16Array | Int8Array | Float64Array;
const ARRAY_TYPES = { Float32Array, Uint32Array, Uint16Array, Uint8Array, Int32Array, Int16Array, Int8Array, Float64Array } as const;
type ArrayType = keyof typeof ARRAY_TYPES;

/** The kept values in call order, each as `[step, value]`, and once read from a file the arrays they index. */
export interface FigureTape { values: [string, Packed][]; arrays?: TypedArray[] }

function pack(value: unknown): Packed {
  if (value === null || value === undefined || typeof value === 'boolean' || typeof value === 'string') return value ?? null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : { $n: String(value) };
  if (ArrayBuffer.isView(value)) {
    if (!(value.constructor.name in ARRAY_TYPES)) throw new Error(`A figure tape cannot keep a ${value.constructor.name}.`);
    // A copy: the builder goes on to change what it was handed.
    return { $a: (value as TypedArray).slice() };
  }
  if (Array.isArray(value)) return value.map(pack);
  if (value instanceof Set) return { $s: [...value].map(pack) };
  if (value instanceof THREE.BufferGeometry) {
    if (Object.keys(value.morphAttributes).length) throw new Error('A figure tape cannot keep morph targets.');
    const attributes: Record<string, Packed> = {};
    for (const [name, attribute] of Object.entries(value.attributes)) {
      if (!(attribute instanceof THREE.BufferAttribute)) throw new Error('A figure tape keeps plain attributes only.');
      attributes[name] = [pack(attribute.array), attribute.itemSize, attribute.normalized];
    }
    return { $g: { name: value.name, attributes, index: value.index ? pack(value.index.array) : null,
      groups: value.groups.map(({ start, count, materialIndex }) => ({ start, count, materialIndex: materialIndex ?? null })), userData: pack(value.userData) } };
  }
  if (Object.getPrototypeOf(value) !== Object.prototype) throw new Error(`A figure tape cannot keep a ${(value as object).constructor.name}.`);
  // An absent field stays absent rather than coming back null.
  return Object.fromEntries(Object.entries(value).flatMap(([key, item]) => item === undefined ? [] : [[key, pack(item)]]));
}

/** `copy` when the tape replays again: the builder changes what it is handed. */
function unpack(packed: Packed, arrays: readonly TypedArray[], copy = false): unknown {
  if (packed === null || typeof packed !== 'object') return packed;
  if (Array.isArray(packed)) return packed.map(item => unpack(item, arrays, copy));
  if (ArrayBuffer.isView(packed)) return copy ? packed.slice() : packed;
  if ('$n' in packed) return Number(packed.$n);
  if ('$a' in packed) { const array = typeof packed.$a === 'number' ? arrays[packed.$a] : packed.$a as TypedArray; return copy ? array.slice() : array; }
  if ('$s' in packed) return new Set((packed.$s as Packed[]).map(item => unpack(item, arrays, copy)));
  if ('$g' in packed) {
    const kept = packed.$g as { name: string; attributes: Record<string, [Packed, number, boolean]>; index: Packed; groups: { start: number; count: number; materialIndex: number | null }[]; userData: Packed };
    const geometry = new THREE.BufferGeometry(); geometry.name = kept.name;
    for (const [name, [array, itemSize, normalized]] of Object.entries(kept.attributes)) geometry.setAttribute(name, new THREE.BufferAttribute(unpack(array, arrays, copy) as TypedArray, itemSize, normalized));
    if (kept.index !== null) geometry.setIndex(new THREE.BufferAttribute(unpack(kept.index, arrays, copy) as TypedArray, 1));
    for (const { start, count, materialIndex } of kept.groups) geometry.addGroup(start, count, materialIndex ?? undefined);
    geometry.userData = unpack(kept.userData, arrays, copy) as Record<string, unknown>;
    return geometry;
  }
  return Object.fromEntries(Object.entries(packed).map(([key, item]) => [key, unpack(item, arrays, copy)]));
}

let recording: FigureTape | null = null, replaying: { tape: FigureTape; at: number; copy: boolean } | null = null;
class TapeMismatch extends Error {}

/**
 * Runs `build`, the figure builder's `step`, or, while a figure replays its tape, returns what it returned when the figure was recorded.
 * The result is the caller's to change.
 */
export function taped<T>(step: string, build: () => T): T {
  if (replaying) {
    const kept = replaying.tape.values[replaying.at++];
    if (!kept) throw new TapeMismatch('the tape ran out');
    if (kept[0] !== step) throw new TapeMismatch(`it kept ${kept[0]} where the builder takes ${step}`);
    // The last figure to replay a tape is handed its arrays (views of the baked file) as they are.
    return unpack(kept[1], replaying.tape.arrays ?? [], replaying.copy) as T;
  }
  const value = build();
  if (recording) recording.values.push([step, pack(value)]);
  return value;
}

/** Builds a figure while keeping its tape (the baker's side). */
export function recordFigure<T>(build: () => T): { result: T; tape: FigureTape } {
  if (recording || replaying) throw new Error('Figures record one at a time.');
  const tape: FigureTape = { values: [] };
  recording = tape;
  try { return { result: build(), tape }; } finally { recording = null; }
}

/** Sorted keys, so equal definitions give equal keys whatever order their fields were written in. */
function stable(value: unknown): string {
  if (value === undefined) return 'null';
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record).filter(key => record[key] !== undefined).sort().map(key => `${JSON.stringify(key)}:${stable(record[key])}`).join(',')}}`;
}
/** 53-bit string hash (cyrb53). */
function hash(text: string) {
  let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i++) { const c = text.charCodeAt(i); h1 = Math.imul(h1 ^ c, 2654435761); h2 = Math.imul(h2 ^ c, 1597334677); }
  h1 = Math.imul(h1 ^ h1 >>> 16, 2246822507) ^ Math.imul(h2 ^ h2 >>> 13, 3266489909);
  h2 = Math.imul(h2 ^ h2 >>> 16, 2246822507) ^ Math.imul(h1 ^ h1 >>> 13, 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}
/**
 * What a figure is built from: its look, its colour (the palette's fallback) and what it carries (`unitEquipment`), with the build options.
 * Names, ids and positions never change a figure, so residents or crew members that look alike share a bake.
 */
export function figureKey(definition: UnitDefinition, options: HumanModelOptions = {}) {
  const { appearance, color, profile, gear, shield, offHand } = definition as UnitDefinition & { gear?: unknown; shield?: unknown; offHand?: unknown };
  return hash(stable({ appearance, color, attacks: profile?.attacks.map(({ id, mode }) => ({ id, mode })), gear, shield, offHand, options }));
}

const installed = new Map<string, { tape: FigureTape; uses: number }>();
/** A figure's tape, replayed by the next `uses` figures built with that key (`withFigureTape`). */
export function installFigureTape(key: string, tape: FigureTape, uses = 1) { installed.set(key, { tape, uses }); }
export function dropFigureTape(key: string) { installed.delete(key); }

/** Builds a figure, replaying its installed tape when it has one; a tape that does not fit is dropped and the figure built live. */
export function withFigureTape<T>(definition: UnitDefinition, options: HumanModelOptions | undefined, build: () => T): T {
  if (recording || replaying || !installed.size) return build();
  const key = figureKey(definition, options), entry = installed.get(key);
  if (!entry) return build();
  const tape = entry.tape;
  if (--entry.uses <= 0) installed.delete(key);
  replaying = { tape, at: 0, copy: entry.uses > 0 };
  try {
    const result = build();
    if (replaying.at !== tape.values.length) throw new TapeMismatch('the tape was left over');
    return result;
  } catch (error) {
    // A builder that throws on a tape would throw on its own too, and does so again below.
    console.warn(`A baked figure did not fit its builder (${error instanceof TapeMismatch ? error.message : error}); building it live.`);
  } finally { replaying = null; }
  return build();
}

const FIGURE_FORMAT = 2;
/**
 * A baked figure file: its head surfaces (`HeadCacheEntries`) and its tape, as JSON over one blob of 8-byte-aligned arrays. Recorded
 * arrays become their index in the blob.
 */
export function encodeFigure(heads: unknown, tape: FigureTape): Uint8Array {
  const arrays: TypedArray[] = [];
  const index = (packed: Packed): Packed => {
    if (packed === null || typeof packed !== 'object') return packed;
    if (ArrayBuffer.isView(packed)) throw new Error('Pack before writing.');
    if (Array.isArray(packed)) return packed.map(index);
    if ('$a' in packed && ArrayBuffer.isView(packed.$a)) { arrays.push(packed.$a); return { $a: arrays.length - 1 }; }
    return Object.fromEntries(Object.entries(packed).map(([key, item]) => [key, index(item)]));
  };
  const values = tape.values.map(([step, value]) => [step, index(value)]), packedHeads = index(pack(heads)), layout: [ArrayType, number, number][] = []; let size = 0;
  for (const array of arrays) { layout.push([array.constructor.name as ArrayType, size, array.length]); size += Math.ceil(array.byteLength / 8) * 8; }
  const header = new TextEncoder().encode(JSON.stringify({ heads: packedHeads, values, layout }));
  const start = 8 + Math.ceil(header.byteLength / 8) * 8, result = new Uint8Array(start + size), view = new DataView(result.buffer);
  view.setUint32(0, FIGURE_FORMAT, true); view.setUint32(4, header.byteLength, true); result.set(header, 8);
  arrays.forEach((array, i) => result.set(new Uint8Array(array.buffer, array.byteOffset, array.byteLength), start + layout[i][1]));
  return result;
}
export function decodeFigure(buffer: ArrayBuffer): { heads: unknown; tape: FigureTape } {
  const view = new DataView(buffer); if (view.getUint32(0, true) !== FIGURE_FORMAT) throw new Error('Unsupported figure bake.');
  const length = view.getUint32(4, true), start = 8 + Math.ceil(length / 8) * 8;
  const { heads, values, layout } = JSON.parse(new TextDecoder().decode(new Uint8Array(buffer, 8, length))) as { heads: Packed; values: [string, Packed][]; layout: [ArrayType, number, number][] };
  const arrays = layout.map(([type, offset, count]) => new ARRAY_TYPES[type](buffer, start + offset, count));
  return { heads: unpack(heads, arrays), tape: { values, arrays } };
}
